import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, Pencil, Trash2, Image as ImageIcon, Check, X,
    ChevronUp, ChevronDown, Loader2, RefreshCw, CheckCircle2, Wand2
} from 'lucide-react';
import { translateToArabic } from '../../utils/translationApi';

export interface ServiceCard {
    index: number; // 1-based
    title: string;
    title_ar: string;
    desc: string;
    desc_ar: string;
    image: string;
}

interface ServiceCardManagerProps {
    section: string;            // e.g. 'Tyres', 'Mining', 'Exchange'
    data: Record<string, any>;  // The section content object from API
    onSaved: () => void;        // Callback to refresh content after save
    sectionTitle?: string;
    sectionTitleAr?: string;
    onChangeSectionTitle?: (en: string) => void;
    onChangeSectionTitleAr?: (ar: string) => void;
}

const API_BASE = '';

const ServiceCardManager: React.FC<ServiceCardManagerProps> = ({
    section,
    data,
    onSaved,
    sectionTitle,
    sectionTitleAr,
    onChangeSectionTitle,
    onChangeSectionTitleAr,
}) => {
    const getToken = () => localStorage.getItem('adminToken') || '';

    // Build initial cards from data keys svc_1_title, svc_2_title, etc.
    const buildCards = (): ServiceCard[] => {
        const defaults = [
            {
                title: "Quality Services",
                desc: "Excellence in service delivery and operations.",
                image: "/banner/banner.jpg"
            },
            {
                title: "Safety & Standards",
                desc: "Adhering to safety compliance and international regulations.",
                image: "/banner/banner2.jpg"
            },
            {
                title: "Customer Focus",
                desc: "Focused on delivering client satisfaction and building long term relations.",
                image: "/banner/banner4.jpg"
            }
        ];

        const hasAnySvcKey = Object.keys(data).some(key => key.startsWith('svc_'));

        if (!hasAnySvcKey) {
            return defaults.map((def, idx) => ({
                index: idx + 1,
                title: def.title,
                title_ar: '',
                desc: def.desc,
                desc_ar: '',
                image: def.image
            }));
        }

        const cards: ServiceCard[] = [];
        
        let maxIdx = 1;
        Object.keys(data).forEach(key => {
            const match = key.match(/^svc_(\d+)_/);
            if (match) {
                const idx = parseInt(match[1]);
                if (idx > maxIdx) maxIdx = idx;
            }
        });

        for (let i = 1; i <= maxIdx; i++) {
            const hasTitle = data[`svc_${i}_title`] !== undefined;
            const hasDesc = data[`svc_${i}_desc`] !== undefined;
            const hasImage = data[`svc_${i}_image`] !== undefined;
            
            if (hasTitle || hasDesc || hasImage) {
                cards.push({
                    index: i,
                    title: data[`svc_${i}_title`] || '',
                    title_ar: data[`svc_${i}_title_ar`] || '',
                    desc: data[`svc_${i}_desc`] || '',
                    desc_ar: data[`svc_${i}_desc_ar`] || '',
                    image: data[`svc_${i}_image`] || '',
                });
            }
        }
        return cards;
    };

    const [cards, setCards] = useState<ServiceCard[]>(buildCards);

    useEffect(() => {
        setCards(buildCards());
    }, [data]);

    const [editingIdx, setEditingIdx] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<Omit<ServiceCard, 'index'>>({ title: '', title_ar: '', desc: '', desc_ar: '', image: '' });
    const [pendingImage, setPendingImage] = useState<{ file: File; preview: string } | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<number | null>(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [translating, setTranslating] = useState<'title' | 'desc' | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const showMsg = (text: string, type: 'success' | 'error') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    const openEdit = (card: ServiceCard) => {
        setEditingIdx(card.index);
        setEditForm({ title: card.title, title_ar: card.title_ar, desc: card.desc, desc_ar: card.desc_ar, image: card.image });
        setPendingImage(null);
    };

    const openAdd = () => {
        const nextIdx = cards.length > 0 ? Math.max(...cards.map(c => c.index)) + 1 : 1;
        setEditingIdx(nextIdx);
        setEditForm({ title: '', title_ar: '', desc: '', desc_ar: '', image: '' });
        setPendingImage(null);
    };

    const cancelEdit = () => {
        setEditingIdx(null);
        setPendingImage(null);
    };

    const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const preview = URL.createObjectURL(file);
        setPendingImage({ file, preview });
        setEditForm(prev => ({ ...prev, image: preview }));
    };

    const handleTranslate = async (field: 'title' | 'desc') => {
        const text = field === 'title' ? editForm.title : editForm.desc;
        if (!text) return;
        setTranslating(field);
        try {
            const translated = await translateToArabic(text, getToken());
            if (translated) {
                setEditForm(prev => ({ ...prev, [`${field}_ar`]: translated }));
            }
        } catch {
            // silent
        } finally {
            setTranslating(null);
        }
    };

    const saveCard = async () => {
        if (!editingIdx) return;
        setSaving(true);
        try {
            const prefix = `svc_${editingIdx}`;
            const token = getToken();

            // Save text fields
            const textFields = [
                { key: `${prefix}_title`, val: editForm.title, ar: editForm.title_ar },
                { key: `${prefix}_desc`, val: editForm.desc, ar: editForm.desc_ar },
            ];
            await Promise.all(textFields.map(f =>
                fetch(`${API_BASE}/api/homepage/update-text`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ section, content_key: f.key, content_value: f.val, content_value_ar: f.ar })
                })
            ));

            // Upload image if pending
            if (pendingImage) {
                const fd = new FormData();
                fd.append('section', section);
                fd.append('content_key', `${prefix}_image`);
                fd.append('image', pendingImage.file);
                const imgRes = await fetch(`${API_BASE}/api/homepage/update-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd,
                });
                const imgData = await imgRes.json();
                if (imgData.imageUrl) {
                    setEditForm(prev => ({ ...prev, image: `${API_BASE}${imgData.imageUrl}` }));
                }
            }

            // Update local cards state
            setCards(prev => {
                const exists = prev.find(c => c.index === editingIdx);
                if (exists) {
                    return prev.map(c => c.index === editingIdx
                        ? { ...c, ...editForm, image: pendingImage ? editForm.image : c.image }
                        : c
                    );
                }
                return [...prev, { index: editingIdx, ...editForm }].sort((a, b) => a.index - b.index);
            });

            setPendingImage(null);
            setEditingIdx(null);
            showMsg('Service card saved!', 'success');
            onSaved();
        } catch (err) {
            showMsg('Save failed. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteCard = async (card: ServiceCard) => {
        if (!window.confirm(`Delete "${card.title || `Service ${card.index}`}"? This cannot be undone.`)) return;
        setDeleting(card.index);
        try {
            const token = getToken();
            // Delete all keys with prefix svc_N_
            await fetch(`${API_BASE}/api/homepage/delete-prefix`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ section, prefix: `svc_${card.index}_` })
            });
            setCards(prev => prev.filter(c => c.index !== card.index));
            showMsg(`Service ${card.index} deleted.`, 'success');
            onSaved();
        } catch {
            showMsg('Delete failed.', 'error');
        } finally {
            setDeleting(null);
        }
    };

    const displayImage = (card: ServiceCard) =>
        card.image
            ? card.image.startsWith('http') ? card.image : `${API_BASE}${card.image}`
            : null;

    return (
        <div className="space-y-6">
            {/* Section Title (optional) */}
            {onChangeSectionTitle && (
                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-5 space-y-3">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Section Title</label>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="relative">
                            <div className="absolute left-3 top-3 text-[10px] font-bold text-slate-300 uppercase">EN</div>
                            <input
                                type="text"
                                value={sectionTitle || ''}
                                onChange={e => onChangeSectionTitle(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-blue-400"
                                placeholder="Our Services"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute right-3 top-3 text-[10px] font-bold text-slate-300 uppercase">AR</div>
                            <input
                                type="text"
                                dir="rtl"
                                value={sectionTitleAr || ''}
                                onChange={e => onChangeSectionTitleAr?.(e.target.value)}
                                className="w-full pr-10 pl-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-blue-400"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Toast message */}
            <AnimatePresence>
                {message.text && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'}`}
                    >
                        {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <X className="w-5 h-5" />}
                        {message.text}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                <AnimatePresence mode="popLayout">
                    {cards.map(card => (
                        <motion.div
                            key={card.index}
                            layout
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden group hover:shadow-md transition-shadow"
                        >
                            {/* Image Area */}
                            <div className="relative h-44 bg-slate-100 overflow-hidden">
                                {displayImage(card) ? (
                                    <img
                                        src={displayImage(card)!}
                                        alt={card.title || `Service ${card.index}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="text-center text-slate-300">
                                            <ImageIcon className="w-10 h-10 mx-auto mb-1 opacity-30" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">No Image</span>
                                        </div>
                                    </div>
                                )}
                                {/* Index badge */}
                                <div className="absolute top-3 left-3 bg-blue-600 text-white text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest shadow-lg">
                                    Service {card.index}
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="p-5">
                                <h4 className="font-black text-slate-800 text-sm tracking-tight truncate">
                                    {card.title || <span className="text-slate-300 italic font-normal">No title</span>}
                                </h4>
                                {card.title_ar && (
                                    <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate text-right font-arabic">{card.title_ar}</p>
                                )}
                                <p className="text-xs text-slate-500 mt-2 line-clamp-2 leading-relaxed">
                                    {card.desc || <span className="italic text-slate-300">No description</span>}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 px-5 pb-5">
                                <button
                                    onClick={() => openEdit(card)}
                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 text-slate-600 hover:text-blue-600 rounded-xl font-bold text-xs transition-all"
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => deleteCard(card)}
                                    disabled={deleting === card.index}
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 border border-red-100 hover:border-red-300 text-red-500 hover:text-red-700 rounded-xl font-bold text-xs transition-all disabled:opacity-50"
                                >
                                    {deleting === card.index
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Trash2 className="w-3.5 h-3.5" />
                                    }
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {/* Add New Card Button */}
                <motion.button
                    layout
                    onClick={openAdd}
                    className="min-h-[280px] rounded-3xl border-2 border-dashed border-slate-200 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/40 text-slate-400 hover:text-blue-600 flex flex-col items-center justify-center gap-3 transition-all group"
                >
                    <div className="w-14 h-14 rounded-2xl bg-white border-2 border-slate-200 group-hover:border-blue-400 flex items-center justify-center shadow-sm transition-all">
                        <Plus className="w-6 h-6" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest">Add Service Card</span>
                </motion.button>
            </div>

            {/* Edit / Add Modal */}
            <AnimatePresence>
                {editingIdx !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={(e) => e.target === e.currentTarget && cancelEdit()}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.92, y: 20 }}
                            transition={{ type: 'spring', damping: 20 }}
                            className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        >
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-slate-100 px-8 py-5 flex items-center justify-between rounded-t-[2rem] z-10">
                                <div>
                                    <h3 className="font-black text-slate-800 text-lg">
                                        {cards.find(c => c.index === editingIdx) ? `Edit Service ${editingIdx}` : `Add New Service`}
                                    </h3>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                                        {section} · Card #{editingIdx}
                                    </p>
                                </div>
                                <button
                                    onClick={cancelEdit}
                                    className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            <div className="p-8 space-y-6">
                                {/* Image Upload */}
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-3">Service Image</label>
                                    <div
                                        className="relative group rounded-2xl overflow-hidden border-2 border-dashed border-slate-200 hover:border-blue-400 bg-slate-50 transition-all cursor-pointer"
                                        style={{ height: 200 }}
                                        onClick={() => fileRef.current?.click()}
                                    >
                                        {(pendingImage?.preview || editForm.image) ? (
                                            <img
                                                src={pendingImage?.preview || (editForm.image.startsWith('http') ? editForm.image : `${API_BASE}${editForm.image}`)}
                                                alt="Service"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                <ImageIcon className="w-10 h-10 mb-2 opacity-40" />
                                                <span className="text-xs font-bold uppercase tracking-widest">Click to upload image</span>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <div className="bg-white rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg">
                                                <RefreshCw className="w-4 h-4 text-blue-600" />
                                                <span className="text-xs font-black text-blue-600 uppercase tracking-wide">Change Image</span>
                                            </div>
                                        </div>
                                        {pendingImage && (
                                            <div className="absolute top-3 right-3 bg-amber-500 text-white text-[8px] font-black px-2.5 py-1 rounded-full shadow-lg uppercase tracking-widest animate-pulse">
                                                Pending Upload
                                            </div>
                                        )}
                                    </div>
                                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
                                </div>

                                {/* Title */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Title</label>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div className="relative">
                                            <div className="absolute left-3 top-3 text-[10px] font-bold text-slate-300 uppercase pointer-events-none">EN</div>
                                            <input
                                                type="text"
                                                value={editForm.title}
                                                onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                                                placeholder="Service title..."
                                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-blue-400 focus:bg-white transition-all"
                                            />
                                        </div>
                                        <div className="relative">
                                            <div className="absolute right-3 top-3 text-[10px] font-bold text-slate-300 uppercase pointer-events-none">AR</div>
                                            <input
                                                type="text"
                                                dir="rtl"
                                                value={editForm.title_ar}
                                                onChange={e => setEditForm(p => ({ ...p, title_ar: e.target.value }))}
                                                className="w-full pr-10 pl-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-blue-400 focus:bg-white transition-all"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleTranslate('title')}
                                                disabled={translating !== null || !editForm.title}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 text-blue-500 hover:text-blue-700 disabled:text-slate-300 transition-colors"
                                                title="Auto-translate to Arabic"
                                            >
                                                {translating === 'title' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Description</label>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div className="relative">
                                            <div className="absolute left-3 top-3 text-[10px] font-bold text-slate-300 uppercase pointer-events-none">EN</div>
                                            <textarea
                                                value={editForm.desc}
                                                onChange={e => setEditForm(p => ({ ...p, desc: e.target.value }))}
                                                placeholder="Service description..."
                                                rows={4}
                                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-blue-400 focus:bg-white transition-all resize-none"
                                            />
                                        </div>
                                        <div className="relative">
                                            <div className="absolute right-3 top-3 text-[10px] font-bold text-slate-300 uppercase pointer-events-none">AR</div>
                                            <textarea
                                                dir="rtl"
                                                value={editForm.desc_ar}
                                                onChange={e => setEditForm(p => ({ ...p, desc_ar: e.target.value }))}
                                                rows={4}
                                                className="w-full pr-10 pl-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-blue-400 focus:bg-white transition-all resize-none"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleTranslate('desc')}
                                                disabled={translating !== null || !editForm.desc}
                                                className="absolute left-2 top-3 p-2 text-blue-500 hover:text-blue-700 disabled:text-slate-300 transition-colors"
                                                title="Auto-translate to Arabic"
                                            >
                                                {translating === 'desc' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-3 pt-2">
                                    <button
                                        onClick={saveCard}
                                        disabled={saving}
                                        className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-2xl font-black text-sm transition-all shadow-lg shadow-blue-100 active:scale-95"
                                    >
                                        {saving
                                            ? <Loader2 className="w-5 h-5 animate-spin" />
                                            : <Check className="w-5 h-5" />
                                        }
                                        {saving ? 'Saving…' : 'Save Service Card'}
                                    </button>
                                    <button
                                        onClick={cancelEdit}
                                        className="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl font-black text-sm transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ServiceCardManager;
